/**
 * Designer toolbar — flow name, save state, Save Draft, Publish, Simulate,
 * Export/Import, validation summary.
 */

import { Link } from "react-router-dom";
import { ArrowLeft, Save, Send, Loader2, Play, AlertTriangle, Download, Upload, LayoutGrid, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { useEditorStore } from "./editorStore";
import { getApiClient } from "../../lib/apiClient";
import { validateFlow, type ValidationError } from "./validator";
import { useSourcesStore } from "../sources/sourcesStore";
import { useSimulatorStore } from "../simulator/simulatorStore";
import { focusCanvasNode } from "./canvasFocus";
import { ThemeToggle } from "../../components/ThemeToggle";
import type { FlowDefinition } from "../../types/flow";

export function Toolbar() {
  const flowId = useEditorStore((s) => s.flowId);
  const flowName = useEditorStore((s) => s.flowName);
  const definition = useEditorStore((s) => s.definition);
  const dirty = useEditorStore((s) => s.dirty);
  const saving = useEditorStore((s) => s.saving);
  const markSaving = useEditorStore((s) => s.markSaving);
  const toggleSimulator = useSimulatorStore((s) => s.toggle);
  const shopifyStores = useSourcesStore((s) => s.shopifyStores);
  const [publishErr, setPublishErr] = useState<string | null>(null);
  const importInput = useRef<HTMLInputElement | null>(null);

  // Build the (sourceType, reference) → owning-flow lookup the validator
  // uses for rule 24 (one-flow-per-source). Sourced from the in-memory
  // sourcesStore which the SourcesPage + trigger picker keep refreshed.
  const bindings = useMemo(() => {
    const shopify: Record<string, { reference: string; flow_id: string; flow_name: string }> = {};
    for (const store of shopifyStores) {
      if (store.bound_to) {
        shopify[store.shop_domain] = {
          reference: store.shop_domain,
          flow_id: store.bound_to.flow_id,
          flow_name: store.bound_to.flow_name,
        };
      }
    }
    return { shopify };
  }, [shopifyStores]);

  const errors = useMemo(
    () =>
      definition
        ? validateFlow(definition, { currentFlowId: flowId ?? undefined, bindings })
        : [],
    [definition, flowId, bindings],
  );

  async function onSaveDraft() {
    if (!flowId || !definition) return;
    markSaving(true);
    try {
      await getApiClient().versions.create(flowId, { definition });
      useEditorStore.setState({ dirty: false });
    } finally {
      markSaving(false);
    }
  }

  async function onPublish() {
    if (!flowId || !definition) return;
    if (errors.length > 0) {
      setPublishErr(`Fix ${errors.length} validation error${errors.length === 1 ? "" : "s"} before publishing.`);
      return;
    }
    setPublishErr(null);
    markSaving(true);
    try {
      const v = await getApiClient().versions.create(flowId, { definition });
      await getApiClient().versions.publish(flowId, v.id);
      useEditorStore.setState({ dirty: false });
    } catch (err) {
      // The orchestrator re-validates server-side and returns 422 with a
      // list of issues. Swallowing that error made Publish look broken —
      // surface the reason so the merchant can fix it.
      const msg = formatPublishError(err);
      setPublishErr(msg);
      console.error("[publish]", err);
    } finally {
      markSaving(false);
    }
  }

  function onExport() {
    if (!flowId || !definition) return;
    const payload = {
      exported_at: new Date().toISOString(),
      studio_version: "phase-1-mvp",
      flow: { id: flowId, name: flowName },
      definition,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${flowName.replace(/[^a-z0-9-_]/gi, "_") || "flow"}.flow.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onImport(file: File) {
    const text = await file.text();
    let parsed: { definition?: FlowDefinition; flow?: { name?: string } };
    try {
      parsed = JSON.parse(text);
    } catch {
      setPublishErr("Imported file is not valid JSON");
      return;
    }
    if (!parsed.definition || parsed.definition.schema_version !== 1) {
      setPublishErr("Imported file is not a valid Cost+ flow export (schema_version must be 1)");
      return;
    }
    // Replace the current flow's draft with the imported definition.
    if (!flowId) return;
    const v = await getApiClient().versions.create(flowId, { definition: parsed.definition });
    useEditorStore.getState().loadFlow(
      flowId,
      parsed.flow?.name ?? flowName,
      v.definition,
    );
    setPublishErr(null);
  }

  return (
    <header className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 px-4 py-2.5">
      <Link
        to="/flows"
        className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
      >
        <ArrowLeft className="w-4 h-4" />
        Flows
      </Link>
      <div className="flex-1 min-w-0">
        <FlowNameEditor flowId={flowId} flowName={flowName} />
        <p className="text-[11px] font-mono text-neutral-500 truncate">{flowId ?? "—"}</p>
      </div>
      {errors.length > 0 ? (
        <IssuesMenu errors={errors} />
      ) : (
        <span className="text-xs text-category-trigger">✓ valid</span>
      )}
      <span className="text-xs text-neutral-500">
        {saving ? "saving…" : dirty ? "unsaved changes" : "saved"}
      </span>
      <button
        onClick={() => useEditorStore.getState().arrange()}
        disabled={!definition}
        className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-50"
        title="Auto-arrange nodes by depth from the trigger"
      >
        <LayoutGrid className="w-4 h-4" />
        Arrange
      </button>
      <button
        onClick={toggleSimulator}
        className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
      >
        <Play className="w-4 h-4" />
        Simulate
      </button>
      <button
        onClick={onExport}
        disabled={!flowId}
        className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-50"
        title="Export this flow as a .flow.json file"
      >
        <Download className="w-4 h-4" />
      </button>
      <button
        onClick={() => importInput.current?.click()}
        disabled={!flowId}
        className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-50"
        title="Import a .flow.json file and replace this flow's definition"
      >
        <Upload className="w-4 h-4" />
      </button>
      <input
        ref={importInput}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onImport(file);
          e.target.value = "";
        }}
      />
      <button
        onClick={onSaveDraft}
        disabled={!flowId || saving || !dirty}
        className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save draft
      </button>
      <button
        onClick={onPublish}
        disabled={!flowId || saving || errors.length > 0}
        className={clsx(
          "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium",
          errors.length > 0
            ? "bg-neutral-200 dark:bg-neutral-800 text-neutral-500 cursor-not-allowed"
            : "bg-category-terminal/10 border border-category-terminal/30 text-category-terminal hover:bg-category-terminal/20",
        )}
        title={errors.length > 0 ? `${errors.length} validation errors` : "Publish this flow"}
      >
        <Send className="w-4 h-4" />
        Publish
      </button>
      {publishErr ? (
        <span className="text-xs text-category-error ml-2">{publishErr}</span>
      ) : null}
      <ThemeToggle />
    </header>
  );
}

/**
 * Turn a raw publish failure into a short, merchant-friendly sentence.
 * The orchestrator returns 422 with { errors: [{node_id, code, message}] }
 * when the server-side validator rejects publish; we surface the count
 * and the first message so the merchant knows what to fix. Full details
 * go to console.error for debugging.
 */
function formatPublishError(err: unknown): string {
  if (err && typeof err === "object") {
    const e = err as {
      status?: number;
      message?: string;
      issues?: Array<{ message?: string }>;
    };
    if (Array.isArray(e.issues) && e.issues.length > 0) {
      const count = e.issues.length;
      const first = e.issues[0].message ?? "validation failed";
      return count === 1
        ? `Publish rejected: ${first}`
        : `Publish rejected: ${count} server-side issues. First: ${first}`;
    }
    if (typeof e.message === "string" && e.message) {
      return `Publish failed: ${e.message}`;
    }
  }
  return "Publish failed — check console for details.";
}

/**
 * Validation-errors dropdown. Shows the full list of blocking issues that
 * stop Publish; clicking an issue pans/zooms the canvas to the offending
 * node and selects it so the merchant can fix it on the spot.
 */
function IssuesMenu({ errors }: { errors: ValidationError[] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const definition = useEditorStore((s) => s.definition);

  // Close on outside-click / Escape so the button feels like a real menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const labelFor = (nodeId: string | null): string => {
    if (!nodeId || !definition) return "Flow";
    const node = definition.nodes[nodeId];
    if (!node) return nodeId;
    const custom = (node as { label?: string }).label;
    return custom?.trim() || node.type.replace(/_/g, " ");
  };

  function onPick(err: ValidationError) {
    setOpen(false);
    if (err.node_id) focusCanvasNode(err.node_id);
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "flex items-center gap-1 text-xs rounded-md px-2 py-1 border",
          "text-category-error border-category-error/30 bg-category-error/5 hover:bg-category-error/10",
        )}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        {errors.length} issue{errors.length === 1 ? "" : "s"}
        <ChevronDown className={clsx("w-3 h-3 transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div
          className="absolute right-0 mt-1 w-[380px] max-h-[60vh] overflow-y-auto z-30 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-xl"
          role="menu"
        >
          <div className="px-3 py-2 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-category-error" />
            <span className="text-xs font-semibold">
              {errors.length} validation issue{errors.length === 1 ? "" : "s"}
            </span>
            <span className="flex-1" />
            <span className="text-[10px] uppercase tracking-wide text-neutral-500">
              blocks publish
            </span>
          </div>
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {errors.map((err, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => onPick(err)}
                  disabled={!err.node_id}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-900/40 disabled:cursor-default disabled:hover:bg-transparent"
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-900 text-neutral-500">
                      rule {err.rule}
                    </span>
                    <span className="text-[11px] font-medium text-neutral-700 dark:text-neutral-300 truncate">
                      {labelFor(err.node_id)}
                    </span>
                    <span className="flex-1" />
                    {err.node_id ? (
                      <span className="text-[10px] text-neutral-400">click to focus →</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-neutral-700 dark:text-neutral-200">
                    {err.message}
                  </p>
                  <p className="text-[10px] font-mono text-neutral-400 mt-0.5 truncate">
                    {err.code}
                    {err.node_id ? ` · ${err.node_id}` : ""}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Inline-editable flow name. Click to edit; Enter or blur saves; Escape cancels.
 */
function FlowNameEditor({
  flowId,
  flowName,
}: {
  flowId: string | null;
  flowName: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(flowName);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep draft synced when the upstream flowName changes (initial load,
  // navigation between flows).
  useEffect(() => {
    if (!editing) setDraft(flowName);
  }, [flowName, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  async function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (!flowId || trimmed === flowName || trimmed.length === 0) {
      setDraft(flowName);
      return;
    }
    try {
      const updated = await getApiClient().flows.update(flowId, { name: trimmed });
      useEditorStore.setState({ flowName: updated.name });
    } catch {
      setDraft(flowName);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-sm font-semibold truncate text-left hover:underline decoration-dotted underline-offset-4 max-w-full"
        title="Click to rename"
      >
        {flowName || "Untitled flow"}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          void commit();
        } else if (e.key === "Escape") {
          setDraft(flowName);
          setEditing(false);
        }
      }}
      className="w-full text-sm font-semibold bg-transparent border-b border-neutral-300 dark:border-neutral-700 focus:outline-none focus:border-neutral-900 dark:focus:border-neutral-100"
    />
  );
}
